<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="visible"
        class="media-zoom-overlay"
        ref="overlayRef"
        tabindex="0"
        @keydown.esc="$emit('close')"
        @keydown.left="prevMedia"
        @keydown.right="nextMedia"
        @click.self="$emit('close')"
      >
        <!-- Touch/Swipe area -->
        <div
          class="zoom-swipe-area"
          ref="swipeAreaRef"
          @touchstart.passive="onTouchStart"
          @touchmove.passive="onTouchMove"
          @touchend="onTouchEnd"
          @click.self="$emit('close')"
        >
          <!-- Media container with transform for swipe animation -->
          <div class="zoom-media-container" :style="swipeStyle">
            <!-- Image -->
            <img
              v-if="isImage"
              :src="currentMedia?.url"
              :alt="currentMedia?.caption || 'Media'"
              class="zoom-media-element"
              @load="onMediaLoad"
            />
            <!-- Video -->
            <video
              v-else-if="isVideo"
              ref="videoRef"
              :src="currentMedia?.url"
              :poster="currentMedia?.poster"
              class="zoom-media-element zoom-video"
              controls
              playsinline
              autoplay
              @loadeddata="onMediaLoad"
            ></video>
            <!-- Audio -->
            <div v-else-if="isAudio" class="zoom-audio-container">
              <div class="zoom-audio-cover" v-if="currentMedia?.cover">
                <img
                  :src="currentMedia.cover"
                  alt="Audio cover"
                  class="zoom-media-element"
                />
              </div>
              <div class="zoom-audio-placeholder" v-else>
                <svg
                  class="zoom-audio-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <audio
                ref="audioRef"
                :src="currentMedia?.url"
                controls
                autoplay
                class="zoom-audio-player"
                @loadeddata="onMediaLoad"
              ></audio>
            </div>
            <!-- Document -->
            <div v-else-if="isDocument" class="zoom-document-container">
              <iframe
                v-if="isPdf"
                :src="currentMedia?.url"
                class="zoom-document-iframe"
                @load="onMediaLoad"
              ></iframe>
              <div v-else class="zoom-document-fallback">
                <svg
                  class="zoom-document-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <use href="@/assets/icons/iconset.svg#file"></use>
                </svg>
                <a
                  :href="currentMedia?.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="zoom-document-link"
                >
                  Open Document
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation dots (subtle, at bottom) -->
        <div v-if="mediaItems.length > 1" class="zoom-nav-dots">
          <span
            v-for="(_, idx) in mediaItems"
            :key="idx"
            class="zoom-dot"
            :class="{ active: idx === currentIndex }"
            @click="goToIndex(idx)"
          ></span>
        </div>

        <!-- Swipe hint (shows briefly on first open) -->
        <div v-if="showSwipeHint" class="zoom-swipe-hint">
          <span>← Swipe to navigate →</span>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";

export default {
  name: "MediaZoomOverlay",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mediaItems: {
      type: Array,
      required: true,
      default: () => [],
    },
    startIndex: {
      type: Number,
      default: 0,
    },
  },
  emits: ["close", "swipe-down", "index-change"],
  setup(props, { emit }) {
    const overlayRef = ref(null);
    const swipeAreaRef = ref(null);
    const videoRef = ref(null);
    const audioRef = ref(null);
    const currentIndex = ref(0);
    const isLoading = ref(true);
    const showSwipeHint = ref(false);

    // Swipe tracking
    const touchStartX = ref(0);
    const touchStartY = ref(0);
    const touchDeltaX = ref(0);
    const touchDeltaY = ref(0);
    const isSwiping = ref(false);

    // Current media item
    const currentMedia = computed(() => {
      return props.mediaItems[currentIndex.value] || null;
    });

    // Media type detection
    const getMediaType = (url) => {
      if (!url) return "unknown";
      const ext = url.split("?")[0].split(".").pop().toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "webp", "avif", "svg"].includes(ext))
        return "image";
      if (["mp4", "webm", "mov", "m4v"].includes(ext)) return "video";
      if (["mp3", "wav", "ogg", "m4a", "aac"].includes(ext)) return "audio";
      if (
        ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"].includes(
          ext,
        )
      )
        return "document";
      return "image"; // Default fallback
    };

    const mediaType = computed(() => {
      if (currentMedia.value?.type) return currentMedia.value.type;
      return getMediaType(currentMedia.value?.url);
    });

    const isImage = computed(() => mediaType.value === "image");
    const isVideo = computed(() => mediaType.value === "video");
    const isAudio = computed(() => mediaType.value === "audio");
    const isDocument = computed(() => mediaType.value === "document");
    const isPdf = computed(() => {
      const url = currentMedia.value?.url || "";
      return url.toLowerCase().endsWith(".pdf");
    });

    // Swipe style for animation
    const swipeStyle = computed(() => {
      if (!isSwiping.value) return {};
      return {
        transform: `translateX(${touchDeltaX.value}px)`,
        transition: "none",
      };
    });

    // Navigation
    const prevMedia = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--;
        emit("index-change", currentIndex.value);
      }
    };

    const nextMedia = () => {
      if (currentIndex.value < props.mediaItems.length - 1) {
        currentIndex.value++;
        emit("index-change", currentIndex.value);
      }
    };

    const goToIndex = (idx) => {
      if (idx >= 0 && idx < props.mediaItems.length) {
        currentIndex.value = idx;
        emit("index-change", currentIndex.value);
      }
    };

    // Touch handlers for swipe
    const onTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      touchStartX.value = e.touches[0].clientX;
      touchStartY.value = e.touches[0].clientY;
      touchDeltaX.value = 0;
      touchDeltaY.value = 0;
      isSwiping.value = true;
    };

    const onTouchMove = (e) => {
      if (!isSwiping.value || e.touches.length !== 1) return;
      touchDeltaX.value = e.touches[0].clientX - touchStartX.value;
      touchDeltaY.value = e.touches[0].clientY - touchStartY.value;
    };

    const onTouchEnd = () => {
      if (!isSwiping.value) return;
      isSwiping.value = false;

      const threshold = 80; // Minimum swipe distance
      const deltaX = touchDeltaX.value;
      const deltaY = touchDeltaY.value;

      // Check if horizontal swipe is dominant
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX < -threshold) {
          // Swipe left -> next
          nextMedia();
        } else if (deltaX > threshold) {
          // Swipe right -> prev
          prevMedia();
        }
      } else {
        // Vertical swipe
        if (deltaY > threshold) {
          // Swipe down -> close or go to next post
          emit("swipe-down");
          emit("close");
        }
      }

      touchDeltaX.value = 0;
      touchDeltaY.value = 0;
    };

    const onMediaLoad = () => {
      isLoading.value = false;
    };

    // Focus overlay when visible
    watch(
      () => props.visible,
      (visible) => {
        if (visible) {
          currentIndex.value = props.startIndex || 0;
          isLoading.value = true;
          nextTick(() => {
            overlayRef.value?.focus();
          });
          // Show swipe hint briefly
          showSwipeHint.value = true;
          setTimeout(() => {
            showSwipeHint.value = false;
          }, 2000);
        }
      },
    );

    // Prevent body scroll when overlay is visible
    watch(
      () => props.visible,
      (visible) => {
        if (visible) {
          document.body.style.overflow = "hidden";
        } else {
          document.body.style.overflow = "";
        }
      },
    );

    onUnmounted(() => {
      document.body.style.overflow = "";
    });

    return {
      overlayRef,
      swipeAreaRef,
      videoRef,
      audioRef,
      currentIndex,
      currentMedia,
      isLoading,
      showSwipeHint,
      isImage,
      isVideo,
      isAudio,
      isDocument,
      isPdf,
      swipeStyle,
      prevMedia,
      nextMedia,
      goToIndex,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onMediaLoad,
    };
  },
};
</script>

<style scoped>
.media-zoom-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
}

.zoom-swipe-area {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.zoom-media-container {
  max-width: 95vw;
  max-height: 95vh;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease-out;
}

.zoom-media-element {
  max-width: 95vw;
  max-height: 95vh;
  object-fit: contain;
  border-radius: var(--radius-sm, 8px);
}

.zoom-video {
  background: #000;
}

/* Audio container */
.zoom-audio-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-5, 16px);
  padding: var(--space-8, 32px);
}

.zoom-audio-cover img {
  max-width: 400px;
  max-height: 400px;
  border-radius: var(--radius-sm, 8px);
}

.zoom-audio-placeholder {
  width: 200px;
  height: 200px;
  background: linear-gradient(
    135deg,
    var(--color-accent-surface, #e8d5ff) 0%,
    var(--color-accent-surface-subtle, #f3e8ff) 100%
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-audio-icon {
  width: 80px;
  height: 80px;
  color: var(--color-accent-primary, #9c27b0);
  opacity: 0.8;
}

.zoom-audio-player {
  width: 100%;
  max-width: 400px;
}

/* Document container */
.zoom-document-container {
  width: 90vw;
  height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-document-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: var(--radius-sm, 8px);
  background: #fff;
}

.zoom-document-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-5, 16px);
  color: #fff;
}

.zoom-document-icon {
  width: 64px;
  height: 64px;
  color: #fff;
  opacity: 0.7;
}

.zoom-document-link {
  color: var(--color-text-on-media);
  text-decoration: underline;
  font-size: var(--font-size-body, 16px);
}

/* Navigation dots */
.zoom-nav-dots {
  position: fixed;
  bottom: var(--space-5, 16px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.zoom-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.zoom-dot:hover {
  background: rgba(255, 255, 255, 0.7);
}

.zoom-dot.active {
  background: #fff;
  transform: scale(1.25);
}

/* Swipe hint */
.zoom-swipe-hint {
  position: fixed;
  bottom: calc(var(--space-5, 16px) + 30px);
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.6);
  font-size: var(--font-size-label, 12px);
  pointer-events: none;
  animation: hint-fade 2s ease forwards;
}

@keyframes hint-fade {
  0% {
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
